// Lifted Views News — article detail page.
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ExternalLink, Clock, Tag, MapPin, AlertCircle } from "lucide-react";
import api from "@/lib/api";
import { ShareBar, ArticlePhoto, ArticleBody } from "@/components/news/ArticleExtras";
import PageMeta from "@/components/PageMeta";
import { ArticleMeta } from "./LiftedViewsNews";

const CATEGORY_LABELS = {
  cannabis: "Cannabis",
  hemp_cbd: "Hemp & CBD",
  vapes_carts: "Vapes & Carts",
  nicotine_disposables: "Nicotine & Disposables",
  state_policy: "State Policy & Bans",
  business: "Business",
  culture: "Culture",
};

export default function LiftedViewsNewsArticle() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [state, setState] = useState({ loading: true, err: "" });

  useEffect(() => {
    api.get(`/news/articles/${slug}`)
      .then((r) => { setArticle(r.data.article); setState({ loading: false, err: "" }); })
      .catch(() => setState({ loading: false, err: "Article not found." }));
  }, [slug]);

  if (state.loading) {
    return <div className="min-h-screen bg-[#04070a] text-white flex items-center justify-center"
                data-testid="news-detail-loading">
      <p>Loading…</p>
    </div>;
  }
  if (state.err || !article) {
    return (
      <div className="min-h-screen bg-[#04070a] text-white container mx-auto px-4 py-20 text-center"
           data-testid="news-detail-not-found">
        <AlertCircle className="h-10 w-10 text-lifted-green mx-auto mb-4" />
        <h1 className="font-anton uppercase text-3xl text-white">Article Not Found</h1>
        <Link to="/lifted-views-news" className="btn-lifted mt-6 inline-flex">
          <ArrowLeft className="h-3 w-3" /> Back to news
        </Link>
      </div>
    );
  }

  const sourceDate = article.source_published_at
    ? new Date(article.source_published_at).toLocaleDateString()
    : null;
  const pubDate = article.published_at
    ? new Date(article.published_at).toLocaleDateString()
    : null;
  const updDate = article.updated_at
    ? new Date(article.updated_at).toLocaleDateString()
    : null;

  return (
    <div className="min-h-screen bg-[#04070a] text-white" data-testid="news-detail-page">
      <PageMeta
        title={`${article.headline} — Lifted Views News`}
        description={article.summary}
        path={`/lifted-views-news/${article.slug}`}
      />
      <div className="container mx-auto px-4 py-10 lg:py-14 max-w-3xl">
        <Link to="/lifted-views-news" data-testid="news-back-link"
          className="inline-flex items-center gap-1 text-xs text-lifted-dim hover:text-lifted-green mb-6">
          <ArrowLeft className="h-3 w-3" /> Back to news
        </Link>
        <div className="flex items-center gap-2 text-[11px] text-lifted-green font-bebas uppercase tracking-[0.2em]">
          <Tag className="h-3 w-3" /> {CATEGORY_LABELS[article.category] || article.category}
          {article.state && <><span>·</span><MapPin className="h-3 w-3" /> {article.state}</>}
          {article.policy_status && <span>· Policy: {article.policy_status}</span>}
        </div>
        <h1 className="font-anton uppercase text-3xl lg:text-5xl text-white mt-3 leading-tight"
            data-testid="news-detail-headline">
          {article.headline}
        </h1>
        <p className="text-lg text-white/70 mt-4" data-testid="news-detail-summary">
          {article.summary}
        </p>

        <ArticleMeta a={article} />
        <ArticlePhoto image={article.image} />
        <ArticleBody body={article.body} />
        <ShareBar article={article} />

        {article.ai_assisted && (
          <div className="mt-8 p-3 border border-white/10 rounded text-[11px] text-white/60"
               data-testid="news-ai-disclosure">
            {article.ai_disclosure || "This summary was drafted with AI assistance and reviewed by a Lifted editor before publication. Sources are linked below."}
          </div>
        )}

        {(article.source_url || article.source_publisher) && (
          <div className="mt-6 glass-panel p-4" data-testid="news-source">
            <div className="eyebrow text-lifted-green mb-2">Source</div>
            {article.source_publisher && (
              <div className="text-sm text-white">{article.source_publisher}</div>
            )}
            {article.source_title && (
              <div className="text-xs text-white/60 mt-1 italic" data-testid="news-source-title">“{article.source_title}”</div>
            )}
            {article.last_checked_at && (
              <div className="text-[11px] text-white/40 mt-1" data-testid="news-last-checked">
                Last checked: {new Date(article.last_checked_at).toLocaleString()}
              </div>
            )}
            {sourceDate && (
              <div className="text-xs text-white/50 mt-1 flex items-center gap-1">
                <Clock className="h-3 w-3" /> Source published: {sourceDate}
              </div>
            )}
            {article.source_url && (
              <a href={article.source_url} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-lifted-green hover:underline mt-2"
                data-testid="news-source-link">
                Read original <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>
        )}

        {article.corrections && article.corrections.length > 0 && (
          <div className="mt-6 glass-panel p-4" data-testid="news-corrections">
            <div className="eyebrow text-lifted-green mb-2">Corrections / Updates</div>
            <ul className="space-y-2 text-xs text-white/70">
              {article.corrections.map((c, i) => (
                <li key={i}><span className="text-white/50">{c.ts?.slice(0, 10)} — </span>{c.note}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-8 flex justify-between text-[11px] text-white/40 border-t border-white/5 pt-5">
          {pubDate && <span>Published {pubDate}</span>}
          {updDate && pubDate !== updDate && <span>Updated {updDate}</span>}
        </div>
      </div>
    </div>
  );
}
